import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpen, ChevronRight, Lock, CheckCircle, Clock, Award,
  Play, ChevronLeft, RotateCcw, Star, FileText, Trophy,
  AlertTriangle, CreditCard, Sparkles, X, Check, ArrowRight,
  Shield, Lightbulb, Info, Scale, ChevronDown, GraduationCap,
  Layers, Package, Zap, Target, Crown, TrendingUp, Tag, Download
} from 'lucide-react';

import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';
import { useAuth } from '../context/AuthContext';

import API from '../utils/api';

var PLAN_BADGE_COLORS = {
  free: { bg: 'bg-zinc-500/15', text: 'text-zinc-400', border: 'border-zinc-500/20' },
  basic: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/20' },
  standard: { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/20' },
  pro: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20' },
};

var TIER_COLORS = {
  beginner: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', accent: '#10B981' },
  intermediate: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', accent: '#3B82F6' },
  expert: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', accent: '#F97316' },
  master: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', accent: '#A855F7' },
};

var TIER_ICONS = { beginner: BookOpen, intermediate: Target, expert: Zap, master: Crown };

/* Glow helpers — kept as no-ops so existing call sites work without color glow.
   Color indicators are conveyed by solid backgrounds/borders elsewhere. */
function tierGlow() {
  return {};
}
function completedGlow() {
  return {};
}
/* Find the highest unlocked tier in a series to determine its glow color */
function seriesGlowTier(tiers) {
  var order = ['master', 'expert', 'intermediate', 'beginner'];
  for (var k = 0; k < order.length; k++) {
    var t = tiers.find(function (x) { return x.tier === order[k] && !x.locked; });
    if (t) return order[k];
  }
  return null;
}

/* ==================== STRUCTURED CONTENT RENDERER ==================== */

function StructuredContent({ content }) {
  if (typeof content === 'string') {
    return content.split('\n\n').map(function (para, i) {
      return <p key={i} className="text-[15px] leading-[22px] text-[#1A1738] mb-4">{para}</p>;
    });
  }
  if (!content || !content.sections) return null;

  return content.sections.map(function (section, i) {
    switch (section.type) {
      case 'paragraph':
        return <p key={i} className="text-[15px] leading-[22px] text-[#1A1738] mb-4">{section.text}</p>;
      case 'heading':
        return <h3 key={i} className="text-base font-bold text-[#1A1738] mt-6 mb-3">{section.text}</h3>;
      case 'bullets':
        return (
          <div key={i} className="mb-4">
            {section.title && <p className="text-[15px] font-semibold text-[#2D2560] mb-2">{section.title}</p>}
            <ul className="space-y-2 ml-1">
              {section.items.map(function (item, j) {
                return (
                  <li key={j} className="flex items-start gap-2.5 text-[15px] leading-[22px] text-[#1A1738]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D2560] mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      case 'callout': {
        var colors = {
          info: 'bg-blue-50 border-blue-200 text-blue-900',
          warning: 'bg-amber-50 border-amber-200 text-amber-900',
          tip: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        };
        var icons = { info: Info, warning: AlertTriangle, tip: Lightbulb };
        var Icon = icons[section.variant] || Info;
        return (
          <div key={i} className={'rounded-xl border p-4 mb-4 flex items-start gap-3 ' + (colors[section.variant] || colors.info)} data-testid="lesson-callout">
            <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-[15px] leading-[22px]">{section.text}</p>
          </div>
        );
      }
      case 'key_takeaways':
        return (
          <div key={i} className="mt-6 mb-4 bg-gradient-to-br from-[#EEEFF8] to-[#FBE9F1] border border-[#DDE1EE] rounded-xl p-5" data-testid="lesson-takeaways">
            <p className="text-xs font-bold text-[#2D2560] uppercase tracking-wider mb-3 flex items-center gap-2">
              <Star className="w-3.5 h-3.5" /> Key Takeaways
            </p>
            <ul className="space-y-2">
              {section.items.map(function (item, j) {
                return (
                  <li key={j} className="flex items-start gap-2.5 text-[15px] leading-[22px] text-[#1A1738]">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      default:
        return null;
    }
  });
}

var LEGAL_DISCLAIMER = "Parentum is not a law firm. No attorney-client relationship is created by using these courses or this application. All courses are for informational and educational purposes only. Nothing in these materials constitutes legal advice or a substitute for the advice of a qualified attorney licensed in your jurisdiction. There is no guarantee that taking these courses will result in any particular court outcome. Laws vary by state and jurisdiction. You are solely responsible for consulting with a licensed attorney regarding your specific legal situation.";

/* ==================== DISCLAIMER BANNER (persistent) ==================== */

function DisclaimerBanner() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5" data-testid="legal-disclaimer-banner">
      <Scale className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <p className="text-[13px] sm:text-[13px] text-[#3A3566] leading-relaxed">{LEGAL_DISCLAIMER}</p>
    </div>
  );
}

/* ==================== DISCLAIMER MODAL ==================== */

function DisclaimerModal({ onAccept, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="disclaimer-modal">
      <div className="card-surface p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Scale className="w-5 h-5 text-amber-400" />
          </div>
          <h2 className="text-lg font-semibold text-[#1A1738]">Legal Disclaimer</h2>
        </div>
        <div className="space-y-3">
          <p className="text-sm text-[#1A1738]/80 leading-relaxed">{LEGAL_DISCLAIMER}</p>
          <div className="bg-[#F6F7FC] rounded-lg p-3 space-y-2 text-xs text-[#1A1738]/70 leading-relaxed">
            <p>By proceeding, you acknowledge and agree that:</p>
            <ul className="space-y-1.5 ml-3 list-disc list-outside">
              <li>You understand Parentum provides educational content only, not legal advice.</li>
              <li>No attorney-client or professional-client relationship exists between you and Parentum.</li>
              <li>Course completion does not guarantee any specific legal outcome.</li>
              <li>You will consult a licensed attorney for advice specific to your situation.</li>
              <li>You are responsible for verifying all information against your jurisdiction's laws.</li>
            </ul>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#1A1738]/70 hover:text-[#1A1738]/90" data-testid="disclaimer-cancel-btn">Cancel</button>
          <button onClick={onAccept} className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-sm font-medium" data-testid="disclaimer-accept-btn">I Understand & Accept</button>
        </div>
      </div>
    </div>
  );
}

/* ==================== SERIES OVERVIEW (Main View) ==================== */

function SeriesOverview({ seriesData, onSelectSeries, pricing, megaBundle, onMegaBundlePurchase }) {
  return (
    <div className="space-y-6" data-testid="series-overview">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1738] mb-2">Mastery Education Platform</h1>
        <p className="text-sm text-[#1A1738]/70">3 Series. 4 Tiers each. Master the art of co-parenting.</p>
      </div>
      <DisclaimerBanner />
      {/* Mobile: horizontal scroll-snap carousel (matches the landing feature carousel).
          Desktop (md+): 4-column grid — 3 series cards + the all-3 mastery bundle card. */}
      <div
        className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto md:overflow-visible snap-x md:snap-none snap-mandatory pb-4 pt-1 -mx-3 px-3 md:mx-0 md:px-0 feature-carousel"
        style={{ scrollbarWidth: 'none' }}
        data-testid="series-carousel"
      >
        {seriesData.map(function (s) {
          var completedCount = s.completed_tiers || 0;
          var totalTiers = s.tier_count || 4;
          var allDone = completedCount >= totalTiers;
          var glowTier = seriesGlowTier(s.tiers);
          var cardGlow = allDone ? completedGlow(0.35) : glowTier ? tierGlow(glowTier, 0.3) : {};
          return (
            <button
              key={s.series_id}
              onClick={function () { onSelectSeries(s); }}
              className="card-surface p-6 text-left hover:-translate-y-1 transition-all group snap-center flex-shrink-0 w-[85vw] sm:w-[340px] md:w-auto h-full flex flex-col"
              style={cardGlow}
              data-testid={'series-card-' + s.series_id}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-[#1A1738] truncate leading-tight">{s.series_title}</h2>
                  <p className="text-[16px] text-[#3A3566] leading-snug mt-1">{s.series_subtitle}</p>
                </div>
              </div>

              {s.description && (
                <p className="text-[17px] text-[#1A1738] leading-[26px] mb-4" data-testid={'series-desc-' + s.series_id}>{s.description}</p>
              )}

              <div className="space-y-3 mb-4">
                {s.tiers.map(function (t) {
                  var tc = TIER_COLORS[t.tier] || TIER_COLORS.beginner;
                  var TIcon = TIER_ICONS[t.tier] || BookOpen;
                  return (
                    <div key={t.course_id} className="flex items-center gap-2.5">
                      <TIcon className={'w-4 h-4 ' + tc.text} />
                      <span className="text-[16px] font-medium text-[#1A1738] flex-1 truncate">{t.tier.charAt(0).toUpperCase() + t.tier.slice(1)}</span>
                      {t.status === 'completed' ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : t.prereq_locked ? (
                        <Lock className="w-4 h-4 text-[#1A1738]/40" />
                      ) : t.payment_locked ? (
                        <CreditCard className="w-4 h-4 text-purple-500" />
                      ) : t.progress > 0 ? (
                        <span className="text-sm text-blue-500 font-semibold">{t.progress}%</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="mb-3 mt-auto">
                <div className="flex items-center justify-between text-sm text-[#3A3566] mb-1">
                  <span>{completedCount}/{totalTiers} tiers</span>
                  <span>{s.overall_progress}%</span>
                </div>
                <div className="h-1.5 bg-[#F6F7FC] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all" style={{ width: s.overall_progress + '%' }} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2D2560]">
                  {s.has_bundle ? 'Bundle Owned' : (function() {
                    var sp = pricing && pricing.series_pricing ? pricing.series_pricing.find(function(p) { return p.series_id === s.series_id; }) : null;
                    var bp = sp ? sp.bundle_pricing.bundle_price : s.bundle_price;
                    return 'Price: $' + bp;
                  })()}
                </span>
                <ChevronRight className="w-5 h-5 text-[#1A1738]/40 group-hover:text-[#2D2560] transition-colors" />
              </div>
            </button>
          );
        })}

        {/* 4th card: All-3 Mastery Series Bundle. Sized and styled exactly like
            the SeriesCard buttons so the 4-up grid reads as siblings. The price
            footer is pinned with `mt-auto` so the card height matches the
            others regardless of how much body content sits above it. */}
        {(function () {
          var seriesOwned = seriesData.filter(function (s) { return s.has_bundle; }).length;
          var seriesCompleted = seriesData.filter(function (s) {
            return (s.completed_tiers || 0) >= (s.tier_count || 4);
          }).length;
          var totalSeries = seriesData.length || 3;
          var pct = totalSeries > 0 ? Math.round((seriesCompleted / totalSeries) * 100) : 0;
          var price = megaBundle && typeof megaBundle.mega_bundle_price !== 'undefined'
            ? megaBundle.mega_bundle_price
            : 300;
          // Savings = sum of the 3 individual series bundle prices minus the
          // mega-bundle price (uses the same pricing data the per-series cards
          // render so the chip stays accurate per plan).
          var individualTotal = 0;
          if (pricing && pricing.series_pricing) {
            pricing.series_pricing.forEach(function (sp) {
              individualTotal += (sp.bundle_pricing && sp.bundle_pricing.bundle_price) || 0;
            });
          }
          var savings = Math.max(0, individualTotal - price);
          var owned = !!(megaBundle && megaBundle.purchased) || seriesOwned >= totalSeries;
          return (
            <button
              key="all-bundle"
              onClick={onMegaBundlePurchase}
              disabled={owned}
              className="card-surface p-6 text-left hover:-translate-y-1 transition-all group snap-center flex-shrink-0 w-[85vw] sm:w-[340px] md:w-auto h-full flex flex-col disabled:opacity-90 disabled:cursor-default"
              style={owned ? completedGlow(0.35) : tierGlow('expert', 0.3)}
              data-testid="series-card-all-bundle"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-[#1A1738] truncate leading-tight">All-3 Mastery Bundle</h2>
                  <p className="text-[16px] text-[#3A3566] leading-snug mt-1">Best value · every series</p>
                </div>
              </div>

              {/* Stack the Best Value + Save $X chips horizontally so the
                  bundle card visually anchors at the same vertical rhythm as
                  the other cards (Best Interests/Co-Parenting/High-Conflict). */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-[0.08em] text-white"
                  style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  data-testid="bundle-best-value-chip">
                  Best Value
                </span>
                {savings > 0 && !owned && (
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-[0.08em] text-white"
                    style={{ background: '#2D2560' }}
                    data-testid="bundle-savings-chip">
                    Save ${savings}
                  </span>
                )}
              </div>

              <p className="text-[17px] text-[#1A1738] leading-[26px] mb-4" data-testid="series-desc-all-bundle">
                Unlock every Mastery Series in one purchase — the most affordable way to complete the full Parentum curriculum and earn all three court-ready certificates.
              </p>

              <div className="space-y-3 mb-4">
                {seriesData.map(function (s, si) {
                  var palette = [TIER_COLORS.beginner, TIER_COLORS.intermediate, TIER_COLORS.expert];
                  var tc = palette[si % palette.length];
                  var seriesDone = (s.completed_tiers || 0) >= (s.tier_count || 4);
                  return (
                    <div key={s.series_id} className="flex items-center gap-2.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: tc.accent }}></span>
                      <span className="text-[16px] font-medium text-[#1A1738] flex-1 truncate">{s.series_title}</span>
                      {seriesDone ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : null}
                    </div>
                  );
                })}
                {/* Fourth row keeps the body content visually balanced with the
                    SeriesCards (which always show 4 tier rows). */}
                <div className="flex items-center gap-2.5">
                  <Award className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span className="text-[16px] font-medium text-[#1A1738] flex-1 truncate">3 court-ready certificates</span>
                </div>
              </div>

              <div className="mb-3 mt-auto">
                <div className="flex items-center justify-between text-sm text-[#3A3566] mb-1">
                  <span>{seriesCompleted}/{totalSeries} series</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1.5 bg-[#F6F7FC] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all" style={{ width: pct + '%' }} />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2D2560]">
                  {owned ? 'Bundle Owned' : 'Bundle Price: $' + price}
                </span>
                {owned ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-[#1A1738]/40 group-hover:text-[#2D2560] transition-colors" />
                )}
              </div>
            </button>
          );
        })()}
      </div>
    </div>
  );
}

/* ==================== TIER VIEW (Inside a Series) ==================== */

function TierView({ series, onSelectTier, onBack, onBundlePurchase, onShowAllBundle, pricing, megaBundle }) {
  var pricesByDiff = pricing && pricing.course_pricing ? pricing.course_pricing.prices_by_difficulty : {};
  var seriesPricing = pricing && pricing.series_pricing ? pricing.series_pricing.find(function(p) { return p.series_id === series.series_id; }) : null;
  var bundlePrice = seriesPricing ? seriesPricing.bundle_pricing.bundle_price : 0;
  var hasPaidTiers = series.tiers.some(function (t) { return t.is_premium && !t.purchased; });

  // Savings the user would gain by upgrading from this single series to the
  // All-3 Mastery Bundle. Mirrors the "Save $X" chip on SeriesOverview.
  var individualTotalAll = 0;
  if (pricing && pricing.series_pricing) {
    pricing.series_pricing.forEach(function (sp) {
      individualTotalAll += (sp.bundle_pricing && sp.bundle_pricing.bundle_price) || 0;
    });
  }
  var megaPrice = megaBundle && typeof megaBundle.mega_bundle_price !== 'undefined' ? megaBundle.mega_bundle_price : 300;
  var megaSavings = Math.max(0, individualTotalAll - megaPrice);
  var megaOwned = !!(megaBundle && megaBundle.purchased);

  var [bundlePromo, setBundlePromo] = useState('');
  var [bundlePromoResult, setBundlePromoResult] = useState(null);
  var [bundlePromoLoading, setBundlePromoLoading] = useState(false);
  var [bundlePromoOpen, setBundlePromoOpen] = useState(false);

  var handleValidateBundlePromo = function () {
    if (!bundlePromo.trim()) return;
    setBundlePromoLoading(true);
    setBundlePromoResult(null);
    axios.post(API + '/billing/validate-promo', { code: bundlePromo.trim(), purchase_type: 'bundle' })
      .then(function (r) { setBundlePromoResult(r.data); })
      .catch(function () { setBundlePromoResult({ valid: false, message: 'Could not validate promo code' }); })
      .finally(function () { setBundlePromoLoading(false); });
  };

  var discountedBundlePrice = bundlePrice;
  if (bundlePromoResult && bundlePromoResult.valid && bundlePromoResult.percent_off) {
    discountedBundlePrice = Math.round(bundlePrice * (1 - bundlePromoResult.percent_off / 100) * 100) / 100;
  }

  return (
    <div data-testid="tier-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90 mb-6" data-testid="back-to-series-btn">
        <ChevronLeft className="w-4 h-4" /> All Series
      </button>

      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-[#1A1738] mb-1">{series.series_title}</h1>
        <p className="text-sm text-[#1A1738]/60 mb-2">{series.series_subtitle}</p>
        {series.description && (
          <p className="text-sm text-[#3A3566] leading-relaxed" data-testid="tier-view-description">{series.description}</p>
        )}
      </div>

      {/* Bundle Offer */}
      {!series.has_bundle && hasPaidTiers && (
        <div className="card-surface p-5 mb-6 border-purple-500/30 bg-gradient-to-r from-purple-500/5 to-pink-500/5" data-testid="bundle-offer">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
            <div className="flex items-center gap-3 flex-1">
              <Package className="w-8 h-8 text-purple-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#1A1738]">Complete Bundle — All 4 Tiers</p>
                {megaSavings > 0 && !megaOwned ? (
                  <button
                    type="button"
                    onClick={onShowAllBundle}
                    className="text-xs text-[#2D2560] hover:text-[#1A1738] underline underline-offset-2 decoration-[#2D2560]/40 hover:decoration-[#2D2560] transition-colors text-left"
                    data-testid="bundle-savings-link"
                  >
                    Save ${megaSavings} when you bundle all three courses
                  </button>
                ) : (
                  <p className="text-xs text-[#1A1738]/60">Unlock every tier in this series</p>
                )}
              </div>
            </div>
            <button
              onClick={function () { onBundlePurchase(bundlePromoResult && bundlePromoResult.valid ? bundlePromo.trim().toUpperCase() : ''); }}
              className="px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors"
              style={{ color: '#FFFFFF' }}
              data-testid="bundle-purchase-btn"
            >
              <CreditCard className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              {bundlePromoResult && bundlePromoResult.valid ? (
                <span style={{ color: '#FFFFFF' }}><span className="line-through opacity-50">${bundlePrice}</span> ${discountedBundlePrice}</span>
              ) : (
                <span style={{ color: '#FFFFFF' }}>Buy Course — ${bundlePrice}</span>
              )}
            </button>
          </div>

          {/* Bundle Promo Code */}
          <div className="pt-3 border-t border-white/[0.06]">
            <button onClick={function () { setBundlePromoOpen(!bundlePromoOpen); }}
              className="text-xs text-[#1A1738]/60 hover:text-[#1A1738]/80 transition-colors flex items-center gap-1"
              data-testid="bundle-promo-toggle">
              <Tag className="w-3 h-3" />
              {bundlePromoOpen ? 'Hide promo code' : 'Have a promo code?'}
            </button>
            {bundlePromoOpen && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={bundlePromo}
                    onChange={function (e) { setBundlePromo(e.target.value.toUpperCase()); setBundlePromoResult(null); }}
                    onKeyDown={function (e) { if (e.key === 'Enter') handleValidateBundlePromo(); }}
                    placeholder="Enter code"
                    className="flex-1 bg-white border border-[#DDE1EE] rounded-lg px-3 py-1.5 text-xs text-[#1A1738] placeholder-[#1A1738]/40 focus:outline-none focus:border-purple-500/40"
                    data-testid="bundle-promo-input"
                  />
                  {bundlePromoResult && bundlePromoResult.valid ? (
                    <button onClick={function () { setBundlePromo(''); setBundlePromoResult(null); }}
                      className="px-2.5 py-1.5 rounded-lg bg-[#F6F7FC] text-[#1A1738]/70 text-xs hover:bg-white/10"
                      data-testid="bundle-promo-clear">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={handleValidateBundlePromo}
                      disabled={!bundlePromo.trim() || bundlePromoLoading}
                      className="px-3 py-1.5 rounded-lg bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-500/25 disabled:opacity-30 whitespace-nowrap"
                      data-testid="bundle-promo-apply">
                      {bundlePromoLoading ? '...' : 'Apply'}
                    </button>
                  )}
                </div>
                {bundlePromoResult && bundlePromoResult.valid && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/25 rounded-lg">
                    <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span className="text-xs text-emerald-400 font-medium">{bundlePromoResult.name}</span>
                  </div>
                )}
                {bundlePromoResult && !bundlePromoResult.valid && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500/10 border border-red-500/25 rounded-lg">
                    <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    <span className="text-xs text-red-400">{bundlePromoResult.message}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tier Cards */}
      <div className="space-y-4">
        {series.tiers.map(function (t, idx) {
          var tc = TIER_COLORS[t.tier] || TIER_COLORS.beginner;
          var TIcon = TIER_ICONS[t.tier] || BookOpen;
          var isCompleted = t.status === 'completed';
          // Use backend prereq_locked flag: only prereq-locked tiers are truly inaccessible
          // Payment-locked tiers (prereq satisfied but unpurchased) are browsable for individual lesson purchases
          var isPrereqLocked = t.prereq_locked === true;
          var isPaymentLocked = t.payment_locked === true && !isPrereqLocked;
          var isClickable = !isPrereqLocked;
          var tierCardGlow = isCompleted ? completedGlow(0.35) : isClickable ? tierGlow(t.tier, 0.3) : {};

          return (
            <button
              key={t.course_id}
              onClick={function () { if (isClickable) onSelectTier(t); }}
              disabled={!isClickable}
              className={'card-surface p-5 w-full text-left transition-all ' + (!isClickable ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-0.5 cursor-pointer')}
              style={!isClickable ? {} : tierCardGlow}
              data-testid={'tier-card-' + t.tier}
            >
              <div className="flex items-center gap-4">
                <div className={'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ' + tc.bg}>
                  {isCompleted ? (
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  ) : isPrereqLocked ? (
                    <Lock className="w-5 h-5 text-[#1A1738]/50" />
                  ) : (
                    <TIcon className={'w-5 h-5 ' + tc.text} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={'text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ' + tc.bg + ' ' + tc.text}>
                      {t.tier}
                    </span>
                    {t.price === 0 ? (
                      <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">FREE</span>
                    ) : t.purchased ? (
                      <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">OWNED</span>
                    ) : (
                      <span className="text-xs text-[#1A1738]/50">${pricesByDiff[t.tier] !== undefined ? pricesByDiff[t.tier] : t.price}</span>
                    )}
                    {t.has_certificate && <Award className="w-3.5 h-3.5 text-amber-400" />}
                  </div>
                  <p className="text-sm font-medium text-[#1A1738] truncate">{t.title}</p>
                  <p className="text-xs text-[#1A1738]/60 mt-0.5">{t.total_lessons} lessons · {t.estimated_hours}h estimated</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  {isCompleted ? (
                    <span className="text-xs text-emerald-400 font-medium">Completed</span>
                  ) : isPrereqLocked ? (
                    <span className="text-xs text-[#1A1738]/50">Locked</span>
                  ) : isPaymentLocked ? (
                    <span className="text-xs text-purple-400 font-medium">Buy Lessons</span>
                  ) : t.progress > 0 ? (
                    <div className="text-right">
                      <span className="text-xs text-[#1A1738]/60">{t.progress}%</span>
                      <div className="w-16 h-1 bg-[#F6F7FC] rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{ width: t.progress + '%' }} />
                      </div>
                    </div>
                  ) : (
                    <ChevronRight className="w-4 h-4 text-[#1A1738]/50" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ==================== COURSE DETAIL VIEW ==================== */

function CourseDetailView({ courseId, onBack, onRefreshSeries, pricing }) {
  var [course, setCourse] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeLesson, setActiveLesson] = useState(null);
  var [showQuiz, setShowQuiz] = useState(false);
  var [showDisclaimer, setShowDisclaimer] = useState(false);
  var [purchasing, setPurchasing] = useState(false);
  var [purchasingLessonId, setPurchasingLessonId] = useState(null);

  var fetchCourse = useCallback(function () {
    setLoading(true);
    axios.get(API + '/courses/' + courseId).then(function (r) {
      setCourse(r.data);
      setLoading(false);

      // Auto-verify lesson purchase from URL
      var params = new URLSearchParams(window.location.search);
      var sessionId = params.get('session_id');
      var purchaseType = params.get('type');
      var lessonId = params.get('lesson_id');
      if (sessionId && purchaseType === 'lesson' && lessonId) {
        axios.post(API + '/courses/' + courseId + '/lessons/' + lessonId + '/checkout/verify', {
          session_id: sessionId
        }).then(function () {
          window.history.replaceState({}, '', window.location.pathname);
          // Re-fetch to update purchase states
          axios.get(API + '/courses/' + courseId).then(function (r2) { setCourse(r2.data); });
        });
      }
    }).catch(function () { setLoading(false); });
  }, [courseId]);

  useEffect(function () { fetchCourse(); }, [fetchCourse]);

  var handleEnroll = function () {
    if (course && !course.disclaimer_acknowledged) {
      setShowDisclaimer(true);
      return;
    }
    axios.post(API + '/courses/' + courseId + '/enroll').then(function () {
      fetchCourse();
    }).catch(function (e) { alert(e.response?.data?.detail || 'Enrollment failed'); });
  };

  var handleDisclaimerAccept = function () {
    axios.post(API + '/courses/' + courseId + '/acknowledge-disclaimer').then(function () {
      setShowDisclaimer(false);
      axios.post(API + '/courses/' + courseId + '/enroll').then(function () { fetchCourse(); });
    });
  };

  var handleLessonComplete = function (lessonId) {
    axios.post(API + '/courses/' + courseId + '/lessons/' + lessonId + '/complete').then(function () {
      fetchCourse();
    });
  };

  var handlePurchase = function () {
    setPurchasing(true);
    var baseUrl = window.location.origin;
    axios.post(API + '/courses/' + courseId + '/purchase', {
      success_url: baseUrl + '/courses',
      cancel_url: baseUrl + '/courses',
    }, { withCredentials: true }).then(function (r) {
      if (r.data.purchased) { setPurchasing(false); fetchCourse(); }
      else if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else { setPurchasing(false); alert('Could not create checkout session'); }
    }).catch(function () { setPurchasing(false); alert('Purchase failed'); });
  };

  var handleLessonPurchase = function (lessonId) {
    setPurchasingLessonId(lessonId);
    var baseUrl = window.location.origin;
    axios.post(API + '/courses/' + courseId + '/lessons/' + lessonId + '/checkout', {
      success_url: baseUrl + '/courses',
      cancel_url: baseUrl + '/courses',
    }, { withCredentials: true }).then(function (r) {
      if (r.data.purchased) { setPurchasingLessonId(null); fetchCourse(); }
      else if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else { setPurchasingLessonId(null); alert('Could not create checkout session'); }
    }).catch(function (e) {
      setPurchasingLessonId(null);
      alert(e.response?.data?.detail || 'Lesson purchase failed');
    });
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!course) return <p className="text-[#1A1738]/60 text-center py-10">Course not found.</p>;

  var allLessons = [];
  (course.modules || []).forEach(function (m) {
    (m.lessons || []).forEach(function (l) { allLessons.push(l); });
  });
  var completedLessons = course.completed_lessons || [];
  var allLessonsDone = completedLessons.length >= allLessons.length;
  var quiz = (course.quizzes || [])[0];
  var quizPassed = quiz && (course.completed_quizzes || []).indexOf(quiz.quiz_id) !== -1;
  var isPremium = course.is_premium;
  var isFullyPurchased = course.purchased;
  var lessonPrice = course.lesson_price || 0;
  var coursePrice = course.dynamic_price !== undefined ? course.dynamic_price : course.price;

  // Prerequisite locked
  if (course.locked && !course.prerequisite_completed) {
    return (
      <div data-testid="course-prereq-wall">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90 mb-6" data-testid="back-to-tiers-btn">
          <ChevronLeft className="w-4 h-4" /> Back to Tiers
        </button>
        <div className="card-surface p-8 text-center max-w-lg mx-auto">
          <Lock className="w-12 h-12 mx-auto mb-4 text-[#1A1738]/50" />
          <h2 className="text-xl font-bold text-[#1A1738] mb-2">Tier Locked</h2>
          <p className="text-sm text-[#1A1738]/70 mb-4">Complete <strong className="text-[#1A1738]">{course.prerequisite_title}</strong> first to unlock this tier.</p>
        </div>
      </div>
    );
  }

  // Active Quiz View
  if (showQuiz && quiz) {
    return (
      <QuizView
        courseId={courseId}
        quiz={quiz}
        isFinalExam={quiz.is_final_exam}
        onBack={function () { setShowQuiz(false); fetchCourse(); }}
        onPass={function () { setShowQuiz(false); fetchCourse(); if (onRefreshSeries) onRefreshSeries(); }}
      />
    );
  }

  // Active Lesson View
  if (activeLesson) {
    var lessonIdx = allLessons.findIndex(function (l) { return l.lesson_id === activeLesson.lesson_id; });
    var prevLesson = lessonIdx > 0 ? allLessons[lessonIdx - 1] : null;
    var nextLesson = lessonIdx < allLessons.length - 1 ? allLessons[lessonIdx + 1] : null;
    var isLessonCompleted = completedLessons.indexOf(activeLesson.lesson_id) !== -1;
    // Check if next lesson is accessible (purchased or full course owned)
    var nextLessonAccessible = nextLesson && (isFullyPurchased || !isPremium || nextLesson.accessible || nextLesson.purchased);
    // Next lesson needs purchasing: premium course, not fully purchased, next lesson exists but not accessible, and current lesson is completed (unlocks it)
    var nextLessonNeedsPurchase = nextLesson && isPremium && !isFullyPurchased && !nextLessonAccessible && isLessonCompleted;

    return (
      <div data-testid="lesson-view">
        <button onClick={function () { setActiveLesson(null); }} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90 mb-6" data-testid="back-to-course-btn">
          <ChevronLeft className="w-4 h-4" /> Back to {course.title}
        </button>
        <div className="max-w-3xl mx-auto">
          <div className="mb-4">
            <p className="text-xs text-[#1A1738]/50 mb-1">Lesson {lessonIdx + 1} of {allLessons.length}</p>
            <h2 className="text-lg font-bold text-[#1A1738]">{activeLesson.title}</h2>
            <p className="text-xs text-[#1A1738]/60 flex items-center gap-2 mt-1">
              <Clock className="w-3 h-3" /> {activeLesson.estimated_minutes} min read
            </p>
          </div>
          <div className="card-surface p-6 mb-6" style={isLessonCompleted ? completedGlow(0.2) : tierGlow(course.tier, 0.2)}>
            <StructuredContent content={activeLesson.content} />
          </div>

          {/* Buy Next Lesson CTA — appears after completing current lesson when next needs purchase */}
          {nextLessonNeedsPurchase && (
            <div className="card-surface p-5 mb-6 border-purple-500/25 bg-gradient-to-r from-purple-500/8 to-pink-500/8" data-testid="buy-next-lesson-cta">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <Sparkles className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1738]">Next: {nextLesson.title}</p>
                    <p className="text-xs text-[#1A1738]/60">
                      {nextLesson.description_preview || (nextLesson.estimated_minutes + ' min read')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={function () { handleLessonPurchase(nextLesson.lesson_id); }}
                  disabled={purchasingLessonId === nextLesson.lesson_id}
                  className="px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium flex items-center gap-2 whitespace-nowrap disabled:opacity-50 transition-colors"
                  data-testid="buy-next-lesson-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  {purchasingLessonId === nextLesson.lesson_id ? 'Processing...' : 'Buy Now — $' + lessonPrice}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              {prevLesson && (
                <button onClick={function () { setActiveLesson(prevLesson); }} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90" data-testid="prev-lesson-btn">
                  <ChevronLeft className="w-4 h-4" /> Previous Lesson
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!isLessonCompleted && (
                <button
                  onClick={function () { handleLessonComplete(activeLesson.lesson_id); }}
                  className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-2"
                  data-testid="complete-lesson-btn"
                >
                  <Check className="w-4 h-4" /> Mark Complete
                </button>
              )}
              {isLessonCompleted && <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Completed</span>}
              {nextLesson && nextLessonAccessible ? (
                <button onClick={function () { handleLessonComplete(activeLesson.lesson_id); setActiveLesson(nextLesson); }} className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-sm font-medium flex items-center gap-2" data-testid="next-lesson-btn">
                  Next Lesson <ChevronRight className="w-4 h-4" />
                </button>
              ) : !nextLesson && quiz ? (
                <button onClick={function () { handleLessonComplete(activeLesson.lesson_id); setActiveLesson(null); setShowQuiz(true); }} className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-sm font-medium flex items-center gap-2" data-testid="go-to-quiz-btn">
                  <FileText className="w-4 h-4" /> Go to Quiz <ChevronRight className="w-4 h-4" />
                </button>
              ) : null}
            </div>
          </div>
          <div className="mt-6">
            <DisclaimerBanner />
          </div>
        </div>
      </div>
    );
  }

  // Course Overview with module/lesson list
  var tc2 = TIER_COLORS[course.tier] || TIER_COLORS.beginner;
  return (
    <div data-testid="course-detail-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90 mb-6" data-testid="back-to-tiers-btn">
        <ChevronLeft className="w-4 h-4" /> Back to Tiers
      </button>

      {/* Course Header */}
      <div className="card-surface p-6 mb-6" style={course.status === 'completed' && quizPassed ? completedGlow(0.3) : tierGlow(course.tier, 0.25)}>
        <div className="flex items-start gap-4 mb-4">
          <div className={'w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ' + tc2.bg}>
            {(TIER_ICONS[course.tier] || BookOpen) && React.createElement(TIER_ICONS[course.tier] || BookOpen, { className: 'w-7 h-7 ' + tc2.text })}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={'text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ' + tc2.bg + ' ' + tc2.text}>
                {course.tier}
              </span>
              {coursePrice === 0 ? (
                <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">FREE</span>
              ) : isFullyPurchased ? (
                <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">OWNED</span>
              ) : (
                <span className="text-xs text-[#1A1738]/50">${coursePrice} full course</span>
              )}
            </div>
            <h1 className="text-lg font-bold text-[#1A1738]">{course.title}</h1>
            <p className="text-sm text-[#1A1738]/60 mt-1">{course.description}</p>
          </div>
        </div>

        {/* Progress */}
        {course.enrolled && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-[#1A1738]/60 mb-1">
              <span>{completedLessons.length}/{allLessons.length} lessons</span>
              <span>{course.progress}%</span>
            </div>
            <div className="h-2 bg-[#F6F7FC] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all" style={{ width: course.progress + '%' }} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {!course.enrolled && !isPremium && (
            <button onClick={handleEnroll} className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-sm font-medium flex items-center gap-2" data-testid="enroll-btn">
              <Play className="w-4 h-4" /> Start Course
            </button>
          )}
          {!course.enrolled && isPremium && isFullyPurchased && (
            <button onClick={handleEnroll} className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 text-sm font-medium flex items-center gap-2" data-testid="enroll-btn">
              <Play className="w-4 h-4" /> Start Course
            </button>
          )}
          {course.enrolled && allLessonsDone && quiz && (
            <button onClick={function () { setShowQuiz(true); }} className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-sm font-medium flex items-center gap-2" data-testid="take-quiz-btn">
              <FileText className="w-4 h-4" /> {quizPassed ? (quiz.is_final_exam ? 'Retake Final Exam' : 'Retake Quiz') : (quiz.is_final_exam ? 'Take Final Exam' : 'Take Quiz')} ({quiz.passing_score}% to pass)
            </button>
          )}
          {course.enrolled && quizPassed && !allLessonsDone && (
            <p className="text-xs text-[#1A1738]/60">Complete remaining lessons to finish this tier.</p>
          )}
          {course.status === 'completed' && quizPassed && (
            <span className="px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm font-medium flex items-center gap-2">
              <Trophy className="w-4 h-4" /> Tier Completed
            </span>
          )}
          {course.status === 'completed' && quizPassed && course.tier === 'master' && (
            <CertificateButton courseId={courseId} />
          )}
        </div>
      </div>

      {/* Full Course Purchase Option (for premium unpurchased) */}
      {isPremium && !isFullyPurchased && (
        <div className="card-surface p-5 mb-4 border-purple-500/25 bg-gradient-to-r from-purple-500/5 to-pink-500/5" data-testid="full-course-purchase-banner">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Package className="w-8 h-8 text-purple-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#1A1738]">Buy Full Course — All {allLessons.length} Lessons</p>
                <p className="text-xs text-[#1A1738]/60">
                  Save vs. buying individually (${lessonPrice}/lesson x {allLessons.length} = ${lessonPrice * allLessons.length})
                </p>
              </div>
            </div>
            <button
              onClick={handlePurchase}
              disabled={purchasing}
              className="px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              data-testid="purchase-full-course-btn"
            >
              <CreditCard className="w-4 h-4" /> {purchasing ? 'Processing...' : 'Buy Full Course — $' + coursePrice}
            </button>
          </div>
        </div>
      )}

      {/* Disclaimer Banner */}
      {!course.disclaimer_acknowledged && !course.enrolled && (
        <div className="card-surface p-4 mb-4 flex items-start gap-3 border-amber-500/20 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300/70">You will be asked to acknowledge a legal disclaimer before enrolling.</p>
        </div>
      )}

      {/* Quiz Status */}
      {quiz && course.enrolled && (
        <div className="card-surface p-4 mb-4 flex items-center justify-between" style={quizPassed ? completedGlow(0.3) : allLessonsDone ? tierGlow(course.tier, 0.25) : {}}>
          <div className="flex items-center gap-3">
            <FileText className={'w-5 h-5 ' + (quizPassed ? 'text-emerald-400' : 'text-[#1A1738]/50')} />
            <div>
              <p className="text-sm font-medium text-[#1A1738]">{quiz.is_final_exam ? 'Final Examination' : 'Tier Quiz'}{quizPassed ? ' — Passed' : ''}</p>
              <p className="text-xs text-[#1A1738]/60">{quiz.question_count || '20+'} questions · {quiz.passing_score}% to pass · {quiz.time_limit} min</p>
            </div>
          </div>
          {quizPassed ? (
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <button onClick={function () { setShowQuiz(true); }} className="text-xs text-[#5A4E9E] hover:underline" data-testid="retake-quiz-link">Retake</button>
            </div>
          ) : allLessonsDone ? (
            <button onClick={function () { setShowQuiz(true); }} className="text-xs text-amber-400 hover:underline" data-testid="quiz-link">Take Quiz</button>
          ) : (
            <Lock className="w-4 h-4 text-[#1A1738]/35" />
          )}
        </div>
      )}

      {/* Module List with Individual Lesson Purchases */}
      {(course.modules || []).map(function (mod) {
        return (
          <div key={mod.module_id} className="mb-5" data-testid={'module-' + mod.module_id}>
            <h3 className="text-xs font-semibold text-[#1A1738]/60 uppercase tracking-wider mb-2 px-1">{mod.title}</h3>
            <div className="space-y-2">
              {(mod.lessons || []).map(function (lesson) {
                var done = completedLessons.indexOf(lesson.lesson_id) !== -1;
                var lessonPurchased = lesson.purchased || isFullyPurchased || !isPremium;
                var seqLocked = lesson.seq_locked && !isFullyPurchased && isPremium;
                var canAccess = lesson.accessible || isFullyPurchased || !isPremium;
                var canPurchase = isPremium && !isFullyPurchased && !lessonPurchased && !seqLocked;
                var lessonGlow = done ? completedGlow(0.25) : canAccess ? tierGlow(course.tier, 0.18) : {};

                return (
                  <div
                    key={lesson.lesson_id}
                    className={'card-surface p-4 transition-all ' + (seqLocked ? 'opacity-50' : '')}
                    style={lessonGlow}
                    data-testid={'lesson-' + lesson.lesson_id}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status Icon */}
                      <div className="mt-0.5 flex-shrink-0">
                        {done ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                        ) : seqLocked ? (
                          <Lock className="w-4 h-4 text-[#1A1738]/35" />
                        ) : canAccess ? (
                          <div className="w-4 h-4 rounded-full border border-white/15" />
                        ) : (
                          <CreditCard className="w-4 h-4 text-purple-400/60" />
                        )}
                      </div>

                      {/* Lesson Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className={'text-sm font-medium ' + (canAccess || done ? 'text-[#1A1738]' : 'text-[#1A1738]/70')}>{lesson.title}</p>
                          <span className="text-xs text-[#1A1738]/40 flex-shrink-0">{lesson.estimated_minutes}m</span>
                        </div>

                        {/* Description preview for unpurchased or locked lessons */}
                        {lesson.description_preview && !canAccess && (
                          <p className="text-xs text-[#1A1738]/50 leading-relaxed mt-1 line-clamp-2">{lesson.description_preview}</p>
                        )}

                        {/* Sequential lock message */}
                        {seqLocked && (
                          <p className="text-xs text-amber-400/60 mt-1 flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Complete the previous lesson to unlock
                          </p>
                        )}
                      </div>

                      {/* Action Area */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {done && (
                          <button
                            onClick={function () { setActiveLesson(lesson); }}
                            className="text-xs text-emerald-400 hover:text-emerald-300 cursor-pointer"
                            data-testid={'review-lesson-' + lesson.lesson_id}
                          >
                            Review
                          </button>
                        )}
                        {!done && canAccess && course.enrolled && (
                          <button
                            onClick={function () { setActiveLesson(lesson); }}
                            className="px-3 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 text-xs font-medium cursor-pointer"
                            data-testid={'open-lesson-' + lesson.lesson_id}
                          >
                            <Play className="w-3 h-3 inline mr-1" />Open
                          </button>
                        )}
                        {canPurchase && (
                          <button
                            onClick={function () { handleLessonPurchase(lesson.lesson_id); }}
                            disabled={purchasingLessonId === lesson.lesson_id}
                            className="px-3.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                            data-testid={'buy-lesson-' + lesson.lesson_id}
                          >
                            <CreditCard className="w-3 h-3" />
                            {purchasingLessonId === lesson.lesson_id ? 'Processing...' : 'Buy Now — $' + lessonPrice}
                          </button>
                        )}
                        {lessonPurchased && !done && !canAccess && (
                          <span className="text-xs text-emerald-400/60">Purchased</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="mt-4">
        <DisclaimerBanner />
      </div>

      {showDisclaimer && <DisclaimerModal onAccept={handleDisclaimerAccept} onClose={function () { setShowDisclaimer(false); }} />}
    </div>
  );
}

/* ==================== QUIZ VIEW ==================== */

function QuizView({ courseId, quiz, isFinalExam, onBack, onPass }) {
  var [questions, setQuestions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [answers, setAnswers] = useState({});
  var [submitted, setSubmitted] = useState(false);
  var [result, setResult] = useState(null);
  var [submitting, setSubmitting] = useState(false);

  useEffect(function () {
    axios.get(API + '/courses/' + courseId + '/quizzes/' + quiz.quiz_id).then(function (r) {
      setQuestions(r.data.questions || []);
      setLoading(false);
    });
  }, [courseId, quiz.quiz_id]);

  var handleSubmit = function () {
    setSubmitting(true);
    axios.post(API + '/courses/' + courseId + '/quizzes/' + quiz.quiz_id + '/submit', { answers: answers }).then(function (r) {
      setResult(r.data);
      setSubmitted(true);
      setSubmitting(false);
      if (r.data.passed && onPass) {
        // Small delay so user sees the result
        setTimeout(function () {}, 500);
      }
    }).catch(function () { setSubmitting(false); });
  };

  var handleRetry = function () {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  // Results view
  if (submitted && result) {
    return (
      <div data-testid="quiz-results">
        <div className="max-w-2xl mx-auto">
          <div className={'card-surface p-8 text-center mb-6 ' + (result.passed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5')}>
            {result.passed ? (
              <>
                <Trophy className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                <h2 className="text-xl font-bold text-[#1A1738] mb-1">{isFinalExam ? 'Examination Passed!' : 'Quiz Passed!'}</h2>
              </>
            ) : (
              <>
                <X className="w-12 h-12 mx-auto mb-3 text-red-400" />
                <h2 className="text-xl font-bold text-[#1A1738] mb-1">Not Quite — Try Again</h2>
              </>
            )}
            <p className="text-2xl font-bold text-[#1A1738]">{result.score}%</p>
            <p className="text-sm text-[#1A1738]/60">{result.correct}/{result.total} correct · {result.passing_score}% required</p>
          </div>

          {/* Question Review */}
          <div className="space-y-4 mb-6">
            {(result.results || []).map(function (r, i) {
              return (
                <div key={i} className={'card-surface p-4 ' + (r.correct ? 'border-emerald-500/20' : 'border-red-500/20')}>
                  <div className="flex items-start gap-3">
                    {r.correct ? <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> : <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="text-sm text-[#1A1738] mb-1">{questions[i]?.question || 'Question ' + (i + 1)}</p>
                      {!r.correct && <p className="text-xs text-emerald-400">Correct: {r.correct_answer}</p>}
                      {r.explanation && <p className="text-xs text-[#1A1738]/60 mt-1">{r.explanation}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center gap-4">
            <button onClick={handleRetry} className="px-5 py-2.5 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-sm font-medium flex items-center gap-2" data-testid="retry-quiz-btn">
              <RotateCcw className="w-4 h-4" /> Retake Quiz
            </button>
            <button onClick={result.passed ? onPass : onBack} className="px-5 py-2.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-2" data-testid="back-to-course-btn-quiz">
              <ArrowRight className="w-4 h-4" /> Back to Course
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz taking view
  var answeredCount = Object.keys(answers).length;
  return (
    <div data-testid="quiz-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#1A1738]/60 hover:text-[#1A1738]/90 mb-6" data-testid="back-from-quiz-btn">
        <ChevronLeft className="w-4 h-4" /> Back to Course
      </button>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-[#1A1738]">{isFinalExam ? 'Final Examination' : quiz.title}</h2>
          <p className="text-xs text-[#1A1738]/60">{questions.length} questions · {quiz.passing_score}% to pass · {quiz.time_limit} min time limit</p>
          <p className="text-xs text-[#1A1738]/50 mt-1">{answeredCount}/{questions.length} answered</p>
        </div>

        <div className="mb-4">
          <DisclaimerBanner />
        </div>

        <div className="space-y-6 mb-8">
          {questions.map(function (q, idx) {
            return (
              <div key={q.question_id} className="card-surface p-5" data-testid={'question-' + q.question_id}>
                <p className="text-sm font-medium text-[#1A1738] mb-3">{idx + 1}. {q.question}</p>
                <div className="space-y-2">
                  {(q.options || []).map(function (opt) {
                    var selected = answers[q.question_id] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={function () {
                          var newAnswers = Object.assign({}, answers);
                          newAnswers[q.question_id] = opt;
                          setAnswers(newAnswers);
                        }}
                        className={'w-full text-left p-3 rounded-lg border text-sm transition-all ' + (selected ? 'border-purple-500/50 bg-purple-500/10 text-white' : 'border-white/10 hover:border-white/20 text-[#1A1738]/80')}
                        data-testid={'option-' + q.question_id + '-' + opt.substring(0, 20).replace(/\s+/g, '-')}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-4">
          <button
            onClick={handleSubmit}
            disabled={answeredCount < questions.length || submitting}
            className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="submit-quiz-btn"
          >
            {submitting ? 'Submitting...' : 'Submit (' + answeredCount + '/' + questions.length + ' answered)'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================== CERTIFICATE BUTTON ==================== */

function CertificateButton({ courseId }) {
  var [cert, setCert] = useState(null);
  var [loading, setLoading] = useState(false);

  var handleGetCert = function () {
    setLoading(true);
    axios.get(API + '/courses/' + courseId + '/certificate').then(function (r) {
      setCert(r.data);
      setLoading(false);
    }).catch(function (e) {
      alert(e.response?.data?.detail || 'Certificate not available');
      setLoading(false);
    });
  };

  if (cert) {
    return (
      <div className="card-surface p-4 border-amber-500/30 bg-amber-500/5 inline-flex items-center gap-3" data-testid="certificate-display">
        <Award className="w-5 h-5 text-amber-400" />
        <div>
          <p className="text-xs font-semibold text-amber-400">Certificate Earned</p>
          <p className="text-xs text-[#1A1738]/60">ID: {cert.certificate_id} · {cert.series_title}</p>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleGetCert}
      disabled={loading}
      className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-sm font-medium flex items-center gap-2"
      data-testid="get-certificate-btn"
    >
      <Award className="w-4 h-4" /> {loading ? 'Loading...' : 'Get Certificate'}
    </button>
  );
}

/* ==================== CERTIFICATES VIEW (Top-level tab) ==================== */
// Lists every certificate the user has earned. Each known series has its own
// certificate artwork. The recipient's name is overlaid onto the artwork in a
// cursive font — the underlying image is NEVER modified.

// Render the certificate (unmodified artwork + cursive name overlay) to a
// landscape US-Letter PDF and trigger a download. The artwork's native size
// is 3300x2550, which is an exact 11x8.5 ratio.
async function downloadCertificatePDF(cert, name, art) {
  // Make sure the cursive font is ready before painting to canvas, otherwise
  // browsers fall back to a generic cursive system font.
  try {
    if (document.fonts && document.fonts.load) {
      await document.fonts.load("180px 'Great Vibes'");
      await document.fonts.ready;
    }
  } catch (e) { /* font.load is best-effort */ }

  var img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise(function (resolve, reject) {
    img.onload = resolve;
    img.onerror = reject;
    img.src = art.image;
  });

  // Use the native artwork resolution so the printed PDF is crisp at 11x8.5".
  var w = img.naturalWidth || 3300;
  var h = img.naturalHeight || 2550;
  var canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  var ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);

  // Cursive name overlay — match the landing/in-app preview styling exactly.
  // 5.2% of the artwork width tracks the on-screen `clamp(.. 5.2cqw ..)` ratio
  // so the printed PDF mirrors what users see on the page.
  var fontSize = Math.round(w * 0.052);
  ctx.fillStyle = '#1A1738';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = fontSize + "px 'Great Vibes', cursive";
  // Bake the in-page vertical padding (0.2em) + line-height (1.4) into the
  // baseline so descenders sit comfortably below the visual midpoint without
  // clipping the surrounding artwork.
  var topY = h * (art.namePositionTopPct / 100);
  var nameY = topY + fontSize * 1.0; // alphabetic baseline ~one em below top
  ctx.fillText(name || '', w / 2, nameY);

  var dataUrl = canvas.toDataURL('image/jpeg', 0.92);

  var jspdfMod = await import('jspdf');
  var jsPDF = jspdfMod.jsPDF || jspdfMod.default;
  var pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' });
  // 11 x 8.5 letter — fits the 3300x2550 artwork exactly.
  pdf.addImage(dataUrl, 'JPEG', 0, 0, 11, 8.5);
  var safeSeries = (cert.series_title || 'certificate').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  pdf.save('parentum-certificate-' + safeSeries + '.pdf');
}

// Map series_id -> { image, namePositionTopPct }. Add new series here as their
// artwork is provided. namePositionTopPct is the vertical % from the top of the
// image where the cursive name should sit (anchored to the signature line in
// the artwork).
var CERTIFICATE_ART = {
  'best-interests': {
    image: '/certificates/best-interests-mastery.png',
    namePositionTopPct: 38, // matches the landing-page showcase placement
  },
  'co-parenting': {
    image: '/certificates/co-parenting-mastery.png',
    namePositionTopPct: 38,
  },
  'high-conflict': {
    image: '/certificates/high-conflict-mastery.png',
    namePositionTopPct: 38,
  },
  // Awarded automatically when the user holds all 3 mastery certs. Issued by
  // the backend with series_id="all-bundle".
  'all-bundle': {
    image: '/certificates/all-bundle-mastery.png',
    namePositionTopPct: 38,
  },
};

function CertificatePreview({ cert }) {
  // Prefer the user's *current* profile name so renaming on the Profile page
  // reflects on every previously-issued certificate (the stored cert.user_name
  // is kept as a historical record / fallback).
  var auth = useAuth();
  var liveName = auth && auth.user && auth.user.name;
  var name = (liveName && String(liveName).trim()) || cert.user_name || '';
  var art = CERTIFICATE_ART[cert.series_id];
  if (!art) {
    // Fallback to the simple metadata card if we don't have artwork for this
    // series yet.
    return (
      <div
        className="card-surface p-5 flex items-start gap-4"
        data-testid={'certificate-card-' + cert.certificate_id}
      >
        <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <Award className="w-6 h-6 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-[#1A1738] truncate">{cert.course_title}</h3>
          <p className="text-sm text-[#3A3566] truncate">{cert.series_title}</p>
          <p className="text-xs text-[#1A1738]/60 mt-1">
            ID: {cert.certificate_id} · Issued {new Date(cert.issued_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface overflow-hidden" data-testid={'certificate-card-' + cert.certificate_id}>
      {/* Aspect-ratio wrapper so the cursive name stays glued to the signature
          line regardless of viewport width. */}
      <div className="relative w-full" style={{ aspectRatio: '3300 / 2550' }}>
        <img
          src={art.image}
          alt={cert.series_title + ' certificate'}
          className="absolute inset-0 w-full h-full object-contain select-none"
          draggable={false}
        />
        <div
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
          style={{ top: art.namePositionTopPct + '%', paddingLeft: '12%', paddingRight: '12%' }}
        >
          <span
            className="text-center"
            style={{
              fontFamily: "'Great Vibes', cursive",
              // Mirrors the landing showcase exactly so the in-app preview reads
              // identically to the marketing screenshot.
              fontSize: 'clamp(28px, 5.2cqw, 88px)',
              lineHeight: 1.4,
              color: '#1A1738',
              padding: '0.2em 0', // breathing room so descenders never clip
            }}
            data-testid={'certificate-name-' + cert.certificate_id}
          >
            {name}
          </span>
        </div>
      </div>
      <div className="p-4 border-t border-[#DDE1EE] flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#1A1738] truncate">{cert.series_title}</p>
          <p className="text-xs text-[#1A1738]/60 truncate">
            ID: {cert.certificate_id} · Issued {new Date(cert.issued_at).toLocaleDateString()}
          </p>
        </div>
        <button
          type="button"
          onClick={function () { downloadCertificatePDF(cert, name, art); }}
          className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2D2560] text-white text-sm font-semibold hover:bg-[#1A1738] transition-colors"
          data-testid={'certificate-download-' + cert.certificate_id}
          aria-label="Download certificate as PDF"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Download PDF</span>
        </button>
      </div>
    </div>
  );
}

function CertificatesView() {
  var [certs, setCerts] = useState(null);

  useEffect(function () {
    axios.get(API + '/courses/user/certificates', { withCredentials: true })
      .then(function (r) { setCerts(Array.isArray(r.data) ? r.data : []); })
      .catch(function () { setCerts([]); });
  }, []);

  if (certs === null) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="certificates-loading">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (certs.length === 0) {
    return (
      <div className="card-surface p-10 text-center" data-testid="certificates-empty">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mx-auto mb-4">
          <Award className="w-7 h-7 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-[#1A1738] mb-2">No certificates yet</h2>
        <p className="text-[15px] text-[#1A1738]/70 max-w-md mx-auto">
          Finish a Mastery Series to earn your first certificate. Completed certificates will appear here for download.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certificates-list">
      <div className="text-center mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1738] mb-2">Your Certificates</h1>
        <p className="text-[15px] text-[#1A1738]/70">{certs.length} earned · keep them for your records</p>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {certs.map(function (c) {
          return <CertificatePreview key={c.certificate_id} cert={c} />;
        })}
      </div>
    </div>
  );
}

/* ==================== MAIN COURSES PAGE ==================== */

export default function Courses() {
  var [seriesData, setSeriesData] = useState([]);
  var [loading, setLoading] = useState(true);
  var [view, setView] = useState('series'); // series | tiers | course
  var [topTab, setTopTab] = useState('courses'); // courses | certificates
  var [selectedSeries, setSelectedSeries] = useState(null);
  var [selectedCourseId, setSelectedCourseId] = useState(null);
  var [pricing, setPricing] = useState(null);
  var [megaBundle, setMegaBundle] = useState(null); // { mega_bundle_price, individual_total, savings, purchased }

  var fetchSeries = useCallback(function () {
    Promise.all([
      axios.get(API + '/courses/series', { withCredentials: true }),
      axios.get(API + '/courses/pricing', { withCredentials: true }).catch(function () { return { data: null }; }),
      axios.get(API + '/courses/series/all/bundle-pricing', { withCredentials: true }).catch(function () { return { data: null }; }),
    ]).then(function (res) {
      setSeriesData(res[0].data);
      if (res[1].data) setPricing(res[1].data);
      if (res[2].data) setMegaBundle(res[2].data);
      setLoading(false);
      if (selectedSeries) {
        var updated = res[0].data.find(function (s) { return s.series_id === selectedSeries.series_id; });
        if (updated) setSelectedSeries(updated);
      }
    }).catch(function () { setLoading(false); });
  }, [selectedSeries?.series_id]);

  useEffect(function () {
    fetchSeries();
    // Verify purchase from URL params
    var params = new URLSearchParams(window.location.search);
    var sessionId = params.get('session_id');
    var purchaseType = params.get('type');
    if (sessionId) {
      if (purchaseType === 'bundle' && selectedSeries) {
        axios.post(API + '/courses/series/' + selectedSeries.series_id + '/bundle-purchase/verify', { session_id: sessionId }).then(function () {
          fetchSeries();
          window.history.replaceState({}, '', window.location.pathname);
        });
      } else if (purchaseType === 'mega-bundle') {
        axios.post(API + '/courses/series/all/bundle-purchase/verify', { session_id: sessionId }).then(function () {
          fetchSeries();
          window.history.replaceState({}, '', window.location.pathname);
        });
      }
      // Individual purchase verification handled at course detail level
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useSyncRefresh(useCallback(function() { fetchSeries(); }, []));  var handleSelectSeries = function (s) {
    setSelectedSeries(s);
    setView('tiers');
  };

  var handleSelectTier = function (t) {
    setSelectedCourseId(t.course_id);
    setView('course');
  };

  // Returning the user from a single-series TierView to the SeriesOverview and
  // scrolling/highlighting the All-3 Mastery Bundle card. Used by the savings
  // link inside the per-series bundle offer.
  var handleShowAllBundle = function () {
    setView('series');
    setSelectedSeries(null);
    setTimeout(function () {
      var el = document.querySelector('[data-testid="series-card-all-bundle"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  var handleBundlePurchase = function (promoCode) {
    if (!selectedSeries) return;
    var baseUrl = window.location.origin;
    var payload = {
      success_url: baseUrl + '/courses',
      cancel_url: baseUrl + '/courses',
    };
    if (promoCode) payload.promo_code = promoCode;
    axios.post(API + '/courses/series/' + selectedSeries.series_id + '/bundle-purchase', payload, { withCredentials: true }).then(function (r) {
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    }).catch(function () { alert('Bundle purchase failed'); });
  };

  // All-3 Mastery Series mega bundle — kicks off Stripe checkout (or returns
  // immediately if the user already owns it / is privileged).
  var handleMegaBundlePurchase = function () {
    if (megaBundle && megaBundle.purchased) return;
    var baseUrl = window.location.origin;
    axios.post(API + '/courses/series/all/bundle-purchase', {
      success_url: baseUrl + '/courses',
      cancel_url: baseUrl + '/courses',
    }, { withCredentials: true }).then(function (r) {
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else if (r.data.purchased) fetchSeries();
    }).catch(function () { alert('Bundle purchase failed'); });
  };

  var handleBackToSeries = function () {
    setView('series');
    setSelectedSeries(null);
    fetchSeries();
  };

  var handleBackToTiers = function () {
    setView('tiers');
    setSelectedCourseId(null);
    fetchSeries();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="courses-page">
      {/* Top-level tabs: Courses vs Certificates. Mirrors the segmented-tab
          pattern used elsewhere in the app so the user can switch between
          enrolling in new material and reviewing what they've earned. */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex p-1 rounded-xl bg-[#F6F7FC] border border-[#DDE1EE]" role="tablist" data-testid="courses-top-tabs">
          <button
            type="button"
            role="tab"
            aria-selected={topTab === 'courses'}
            onClick={function () { setTopTab('courses'); }}
            className={'px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ' +
              (topTab === 'courses' ? 'bg-[#2D2560] text-white shadow-sm' : 'text-[#1A1738] hover:text-[#2D2560]')}
            data-testid="courses-tab-courses"
          >
            <GraduationCap className="w-4 h-4" /> Courses
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={topTab === 'certificates'}
            onClick={function () { setTopTab('certificates'); }}
            className={'px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ' +
              (topTab === 'certificates' ? 'bg-[#2D2560] text-white shadow-sm' : 'text-[#1A1738] hover:text-[#2D2560]')}
            data-testid="courses-tab-certificates"
          >
            <Award className="w-4 h-4" /> Certificates
          </button>
        </div>
      </div>

      {topTab === 'certificates' ? (
        <CertificatesView />
      ) : (
        <>
          {/* Dynamic Pricing Banner */}
          {pricing && pricing.course_pricing.upgrade_message && (
        <div className="mb-6 bg-gradient-to-r from-[#F59E0B]/10 to-[#F97316]/10 border border-[#F59E0B]/20 rounded-xl p-4 flex items-center justify-between" data-testid="pricing-upgrade-banner">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
            <div>
              <p className="text-sm font-medium text-[#F59E0B]">{pricing.course_pricing.upgrade_message}</p>
              <p className="text-xs text-[#1A1738]/50">Your current price: ${pricing.course_pricing.current_price}/course</p>
            </div>
          </div>
          <button onClick={function() { window.dispatchEvent(new CustomEvent('navigate-billing')); }}
            className="px-4 py-1.5 text-xs font-medium bg-[#F59E0B]/20 border border-[#F59E0B]/30 text-[#F59E0B] rounded-lg hover:bg-[#F59E0B]/30"
            data-testid="pricing-upgrade-btn">
            Upgrade
          </button>
        </div>
      )}

      {view === 'series' && (
        <SeriesOverview
          seriesData={seriesData}
          onSelectSeries={handleSelectSeries}
          pricing={pricing}
          megaBundle={megaBundle}
          onMegaBundlePurchase={handleMegaBundlePurchase}
        />
      )}
      {view === 'tiers' && selectedSeries && (
        <TierView
          series={selectedSeries}
          onSelectTier={handleSelectTier}
          onBack={handleBackToSeries}
          onBundlePurchase={handleBundlePurchase}
          onShowAllBundle={handleShowAllBundle}
          pricing={pricing}
          megaBundle={megaBundle}
        />
      )}
      {view === 'course' && selectedCourseId && (
        <CourseDetailView
          courseId={selectedCourseId}
          onBack={handleBackToTiers}
          onRefreshSeries={fetchSeries}
          pricing={pricing}
        />
      )}
        </>
      )}
    </div>
  );
}
